/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  api,
  clearAuth,
  hasAuth,
  setToken,
  UNAUTHORIZED_EVENT,
  type AuthUser,
} from "./api";

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  applyToken: (token: string) => Promise<void>;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!hasAuth()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.me();
      setUser(res.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const applyToken = useCallback(
    async (token: string) => {
      setToken(token);
      setLoading(true);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await api.logout().catch(() => {});
    clearAuth();
    setUser(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onUnauthorized = () => setUser(null);
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, applyToken, refresh, logout }),
    [user, loading, applyToken, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}

export function isAdmin(user: AuthUser | null | undefined): boolean {
  return user?.role === "admin" || user?.role === "superadmin";
}

export function isSuperAdmin(user: AuthUser | null | undefined): boolean {
  return user?.role === "superadmin";
}

export function canAccessArea(
  user: AuthUser | null | undefined,
  areaId: string,
): boolean {
  if (!user) return false;
  if (user.areaIds === "*") return true;
  return user.areaIds.includes(areaId);
}
